import "./DownloadProgressToaster.scss";

import { useState } from "react";

export type DownloadProgress = {
  id: string;
  filename: string;
  totalBytes: number;
  downloadedBytes: number;
  percentage: number;
  speedBytesPerSecond: number;
  estimatedSecondsRemaining: number;
  status: "downloading" | "complete" | "error" | "cancelled";
  errorMessage?: string;
};

type DownloadProgressToasterProps = {
  download: DownloadProgress;
  onCancel?: () => void;
  onDismiss?: () => void;
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
};

const formatSpeed = (bytesPerSecond: number): string => {
  return `${formatBytes(bytesPerSecond)}/s`;
};

const formatTime = (seconds: number): string => {
  if (seconds < 0 || !isFinite(seconds)) return "calculating...";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const DownloadProgressToaster = ({
  download,
  onCancel,
  onDismiss,
}: DownloadProgressToasterProps) => {
  const [isMinimized, setIsMinimized] = useState(false);

  if (isMinimized) {
    const getMinimizedIcon = () => {
      if (download.status === "complete") return "check_circle";
      if (download.status === "error") return "error";
      if (download.status === "cancelled") return "cancel";
      return "download";
    };

    const getMinimizedText = () => {
      if (download.status === "complete") return "Done";
      if (download.status === "error") return "Failed";
      if (download.status === "cancelled") return "Cancelled";
      return `${download.percentage}%`;
    };

    return (
      <div
        className={`DownloadProgressToaster DownloadProgressToaster--minimized DownloadProgressToaster--${download.status}`}
      >
        <div className="download-minimized-content">
          <span className="material-icons">{getMinimizedIcon()}</span>
          <span className="download-minimized-text">{getMinimizedText()}</span>
          <button
            className="download-action-button"
            onClick={() => setIsMinimized(false)}
            aria-label="Expand"
          >
            <span className="material-icons">expand_less</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`DownloadProgressToaster DownloadProgressToaster--${download.status}`}
    >
      <div className="download-header">
        <div className="download-header-left">
          <span className="material-icons download-icon">
            {download.status === "downloading" && "download"}
            {download.status === "complete" && "check_circle"}
            {download.status === "error" && "error"}
            {download.status === "cancelled" && "cancel"}
          </span>
          <div className="download-info">
            <div className="download-filename" title={download.filename}>
              {download.filename}
            </div>
            {download.status === "error" && download.errorMessage && (
              <div className="download-error-message">
                {download.errorMessage}
              </div>
            )}
            {download.status === "cancelled" && (
              <div className="download-cancelled-message">
                Download cancelled
              </div>
            )}
          </div>
        </div>
        <div className="download-header-actions">
          <button
            className="download-action-button"
            onClick={() => setIsMinimized(true)}
            aria-label="Minimize"
          >
            <span className="material-icons">minimize</span>
          </button>
          {download.status === "downloading" && onCancel && (
            <button
              className="download-action-button download-cancel-button"
              onClick={onCancel}
              aria-label="Cancel download"
            >
              <span className="material-icons">close</span>
            </button>
          )}
          {(download.status === "complete" ||
            download.status === "error" ||
            download.status === "cancelled") &&
            onDismiss && (
              <button
                className="download-action-button"
                onClick={onDismiss}
                aria-label="Dismiss"
              >
                <span className="material-icons">close</span>
              </button>
            )}
        </div>
      </div>

      {download.status === "downloading" && (
        <>
          <div className="download-progress-container">
            <div
              className="download-progress-bar"
              style={{ width: `${download.percentage}%` }}
            >
              <div className="download-progress-shine" />
            </div>
          </div>

          <div className="download-stats">
            <div className="download-stat download-percentage">
              {download.percentage}%
            </div>
            <div className="download-stat download-sizes">
              {formatBytes(download.downloadedBytes)} /{" "}
              {formatBytes(download.totalBytes)}
            </div>
            {download.speedBytesPerSecond > 0 && (
              <>
                <div className="download-stat download-speed">
                  {formatSpeed(download.speedBytesPerSecond)}
                </div>
                <div className="download-stat download-eta">
                  ~{formatTime(download.estimatedSecondsRemaining)}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {download.status === "complete" && (
        <div className="download-complete-message">
          Download complete • {formatBytes(download.totalBytes)}
        </div>
      )}
    </div>
  );
};

export default DownloadProgressToaster;
